import { ReactNode } from 'react'; 
import { Box, BoxProps, FormControl, MenuItem, MenuProps, Select, SelectProps } from '@mui/material'; 
import { useTheme } from "@mui/material/styles"; 
 
type DropdownOption = { 
	label: string; 
	value: string; 
	icon?: string; 
	isDisabled?: boolean; 
}; 
 
type CustomDropdownProps = { 
	dropDownPlaceholder?: string; 
	value: string; 
	boldSelectedValue?: boolean; 
	handleChange: (event: any) => void; 
	boxProps?: BoxProps; 
	menuProps?: Partial<MenuProps>; 
	selectComponentProps?: SelectProps; 
	noSelectionLabel?: string; 
	dropdownIcon?: React.ElementType; 
	disabled?: boolean; 
	dropDownStyle?: any; 
	className?: any; 
	textColor?: string; 
	selectedTextColor?: string;
	iconColor?: string; 
	bgColor?: string; 
	borderColor?: string; 
	hoverBgColor?: string; 
	hoverTextColor?: string; 
	selectedBgColor?: string; 
} & ({ options: DropdownOption[]; children?: never } | { children: ReactNode; options?: never }); 
 
const CustomDropdown = ({ 
	value, 
	options, 
	handleChange, 
	dropDownPlaceholder, 
	boxProps, 
	menuProps, 
	selectComponentProps, 
	noSelectionLabel, 
	boldSelectedValue = false, 
	children, 
	dropdownIcon, 
	disabled, 
	dropDownStyle, 
	className, 
	textColor, 
	selectedTextColor,
	iconColor, 
	bgColor, 
	borderColor, 
	hoverBgColor, 
	hoverTextColor, 
	selectedBgColor 
}: CustomDropdownProps) => { 
	const { sx, ...props } = boxProps || {}; 
	const theme = useTheme(); 
	const colors = theme.palette; 
 
	return ( 
		<Box 
			sx={{ 
				backgroundColor: bgColor ? `${bgColor}` : `${colors.backgroundLight.dark}`, 
				borderRadius: '4px', 
				border: borderColor ? `1px solid ${borderColor}` : undefined, 
				zIndex: 9, 
				minWidth: '90px', 
				textAlign: 'left', 
				height: 'fit-content', 
				...sx 
			}} 
			className='custom-dropdown-box' 
			{...props} 
		> 
			<FormControl fullWidth> 
				<Select disabled = {disabled} 
					value={value} 
					style={dropDownStyle} 
					onChange={handleChange} 
					placeholder={dropDownPlaceholder} 
					IconComponent={dropdownIcon ? dropdownIcon : undefined} 
					MenuProps={{ 
						...menuProps, 
						PaperProps: { 
							...menuProps?.PaperProps, 
							sx: { 
								minWidth: className === 'total-cost-spends-dropdown' 
									? 'max-content' 
									: undefined, 
								backgroundColor: bgColor ? `${bgColor}` : `${colors.dropdown.menuItemBackground}`, 
								borderRadius: '4px', 
								...(menuProps?.PaperProps?.sx ), 
							}, 
						}, 
						MenuListProps: { 
							...menuProps?.MenuListProps, 
							sx: { 
								paddingTop: 0, 
								paddingBottom: 0, 
								...(menuProps?.MenuListProps?.sx ), 
							}, 
						}, 
					}} 
					className={className} 
					{...(!!options && { 
						renderValue: (selected) => { 
							if (!selected) { 
								return <em>{noSelectionLabel}</em>; 
							} 

							const selectedOption = options.find((option) => option.value === selected);

							let selectedValueColor;

							if (selectedTextColor) {
								selectedValueColor = `${selectedTextColor} !important`;
							} else if (textColor) {
								selectedValueColor = `${textColor} !important`;
							}
 
							return ( 
								<Box 
									sx={{ 
										display: 'flex', 
										alignItems: 'center', 
										overflow: 'hidden', 
										textOverflow: 'ellipsis', 
										whiteSpace: 'nowrap', 
										color: selectedValueColor,
										...(!boldSelectedValue && theme.typography.filterText), 
									}} 
								> 
									{!!selectedOption?.icon && <Box component="img" src={selectedOption.icon} alt="" sx={{ mr: 1 }} />} 
									{selectedOption?.label} 
								</Box> 
							); 
						} 
					})} 
					{...selectComponentProps} 
					sx={{ 
						...theme.typography.filterText, 
						color: textColor ? `${textColor} !important` : undefined, 
						'& .MuiSelect-select': { 
							color: textColor ? `${textColor} !important` : undefined, 
						}, 
						'& .MuiSelect-icon': { 
							color: (iconColor || textColor) ? `${iconColor || textColor} !important` : undefined, 
						}, 
						backgroundColor: disabled ? `${colors.dropdown.bgColorDisabled} !important` : undefined, 
						...selectComponentProps?.sx 
					}} 
				> 
					{!!options 
						? options.flatMap(({ label, value: optionValue, icon, isDisabled = false }, index) => { 
							const elements = []; 
							if (index === 1 && optionValue === 'commitAll') { 
								elements.push( 
									<Box 
										key={`separator-${index}`} 
										sx={{ 
											height: '4px', 
											backgroundColor: '#003771', 
										}} 
									/> 
								); 
							} 
							elements.push( 
								<MenuItem key={optionValue} value={optionValue} disabled={isDisabled} 
									onClick={() => { 
										if (optionValue === value && ['commit', 'commitAll'].includes(optionValue)) { 
											handleChange({ target: { value: optionValue } }); 
										} 
									}} 
									sx={{ 
										...theme.typography.filterText, 
										padding: className === 'total-cost-spends-dropdown' 
											? '6px 16px' 
											: '4px 8px', 
										minHeight: className === 'total-cost-spends-dropdown' 
											? 'unset' 
											: undefined, 
										backgroundColor: bgColor ? `${bgColor} !important` : `${colors.dropdown.menuItemBackground} !important`, 
										color: textColor ? `${textColor} !important` : `${colors.dropdown.menuItemText} !important`, 
										cursor: isDisabled ? 'not-allowed' : 'pointer', 
										'&.Mui-disabled': { 
											opacity: 0.7, 
											color: textColor ? `${textColor} !important` : `${colors.dropdown.menuItemText} !important`, 
											backgroundColor: bgColor ? `${bgColor} !important` : `${colors.dropdown.menuItemBackground} !important`, 
										}, 
										'&.Mui-selected': { 
											backgroundColor: selectedBgColor ? `${selectedBgColor} !important` : `${colors.dropdown.menuItemSelected} !important`, 
											color: hoverTextColor ? `${hoverTextColor} !important` : `${colors.dropdown.menuItemHoverText} !important`, 
										}, 
										'&.Mui-selected:hover': { 
											backgroundColor: hoverBgColor ? `${hoverBgColor} !important` : `${colors.dropdown.menuItemHover} !important`, 
											color: hoverTextColor ? `${hoverTextColor} !important` : `${colors.dropdown.menuItemHoverText} !important`, 
										}, 
										'&:hover': { 
											backgroundColor: hoverBgColor ? `${hoverBgColor} !important` : `${colors.dropdown.menuItemHover} !important`, 
											color: hoverTextColor ? `${hoverTextColor} !important` : `${colors.dropdown.menuItemHoverText} !important`, 
										}, 
									}}> 
									<Box sx={{ display: 'flex', alignItems: 'center' }}> 
										{!!icon && <Box component="img" src={icon} alt="" sx={{ mr: 1 }} />} 
										{label} 
									</Box> 
								</MenuItem> 
							); 
							return elements; 
						}) 
					: children}  
				</Select> 
			</FormControl> 
		</Box> 
	); 
}; 
 
export default CustomDropdown;
